/**
 * React Query mutations for epics
 * Story 198: View and filter stories in backlog
 * Story 356: Web event instrumentation
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { epicKeys } from './useEpics';
import { track } from '../lib/analytics';

/** Parameters for creating an epic */
interface CreateEpicParams {
  projectSlug: string;
  slug: string;
  title: string;
  description?: string;
}

/** Parameters for updating an epic */
interface UpdateEpicParams {
  projectSlug: string;
  epicSlug: string;
  title?: string;
  description?: string;
}

/** Parameters for deleting an epic */
interface DeleteEpicParams {
  projectSlug: string;
  epicSlug: string;
}

/** Parameters for reordering epics */
interface ReorderEpicsParams {
  projectSlug: string;
  activeSlug: string;
  overSlug: string;
  edge: 'top' | 'bottom';
}

/**
 * Mutation to create a new epic
 */
export function useCreateEpic() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ projectSlug, slug, title, description }: CreateEpicParams) => {
      return api.fetch(`/api/v1/projects/${encodeURIComponent(projectSlug)}/epics`, {
        method: 'POST',
        body: JSON.stringify({ slug, title, description }),
      });
    },
    onSuccess: (_, { projectSlug, slug, title }) => {
      // Story 356: Track epic creation
      track({
        name: 'epic_created',
        properties: { slug, title },
      });
      queryClient.invalidateQueries({ queryKey: epicKeys.list(projectSlug) });
    },
  });
}

/**
 * Mutation to update an epic's title or description
 */
export function useUpdateEpic() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ projectSlug, epicSlug, title, description }: UpdateEpicParams) => {
      return api.fetch(
        `/api/v1/projects/${encodeURIComponent(projectSlug)}/epics/${encodeURIComponent(epicSlug)}`,
        {
          method: 'PATCH',
          body: JSON.stringify({ title, description }),
        }
      );
    },
    onSuccess: (_, { projectSlug, epicSlug, title, description }) => {
      // Story 356: Track epic update with changed fields
      const fieldsChanged: string[] = [];
      if (title !== undefined) fieldsChanged.push('title');
      if (description !== undefined) fieldsChanged.push('description');
      track({
        name: 'epic_updated',
        properties: { slug: epicSlug, fields_changed: fieldsChanged },
      });
      queryClient.invalidateQueries({ queryKey: epicKeys.list(projectSlug) });
    },
  });
}

/**
 * Mutation to delete an epic
 */
export function useDeleteEpic() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ projectSlug, epicSlug }: DeleteEpicParams) => {
      return api.fetch(
        `/api/v1/projects/${encodeURIComponent(projectSlug)}/epics/${encodeURIComponent(epicSlug)}`,
        {
          method: 'DELETE',
        }
      );
    },
    onSuccess: (_, { projectSlug, epicSlug }) => {
      // Story 356: Track epic deletion
      track({
        name: 'epic_deleted',
        properties: { slug: epicSlug },
      });
      queryClient.invalidateQueries({ queryKey: epicKeys.list(projectSlug) });
    },
  });
}

/** Epic list item type for cache manipulation */
interface EpicListItem {
  id: string;
  slug: string;
  title: string;
  description: string | null;
  order_index: number;
  story_count: number;
}

/**
 * Mutation to reorder epics via drag-drop
 * Uses optimistic update for immediate feedback with View Transitions API
 */
export function useReorderEpics() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ projectSlug, activeSlug, overSlug, edge }: ReorderEpicsParams) => {
      return api.fetch(
        `/api/v1/projects/${encodeURIComponent(projectSlug)}/epics/reorder`,
        {
          method: 'POST',
          body: JSON.stringify({ activeSlug, overSlug, edge }),
        }
      );
    },
    onMutate: async ({ projectSlug, activeSlug, overSlug, edge }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: epicKeys.list(projectSlug) });

      // Snapshot the previous value
      const previousEpics = queryClient.getQueryData<EpicListItem[]>(epicKeys.list(projectSlug));

      // Optimistically update to the new order
      if (previousEpics) {
        const sorted = [...previousEpics].sort((a, b) => a.order_index - b.order_index);
        const currentOrder = sorted.map(e => e.slug);

        // Remove active epic and insert at new position
        const newOrder = currentOrder.filter(slug => slug !== activeSlug);
        const overIndex = newOrder.indexOf(overSlug);
        const insertIndex = edge === 'top' ? overIndex : overIndex + 1;
        newOrder.splice(insertIndex, 0, activeSlug);

        // Update order_index values
        const updatedEpics = previousEpics.map(epic => ({
          ...epic,
          order_index: newOrder.indexOf(epic.slug),
        }));

        queryClient.setQueryData(epicKeys.list(projectSlug), updatedEpics);
      }

      // Return context with previous value for rollback
      return { previousEpics };
    },
    onError: (_, { projectSlug }, context) => {
      // Rollback on error
      if (context?.previousEpics) {
        queryClient.setQueryData(epicKeys.list(projectSlug), context.previousEpics);
      }
    },
    onSuccess: (_, { projectSlug, activeSlug, overSlug: _overSlug, edge: _edge }) => {
      // Story 356: Track epic reorder
      // Calculate new position from current cache state
      const epics = queryClient.getQueryData<EpicListItem[]>(epicKeys.list(projectSlug));
      const newPosition = epics?.find(e => e.slug === activeSlug)?.order_index ?? 0; // FALLBACK: Analytics non-blocking
      track({
        name: 'epic_reordered',
        properties: { slug: activeSlug, new_position: newPosition },
      });
    },
    onSettled: (_, __, { projectSlug }) => {
      // Always refetch to ensure server state
      queryClient.invalidateQueries({ queryKey: epicKeys.list(projectSlug) });
    },
  });
}
